import { useState } from 'react';
import type { SiteReport } from '@/mocks/actualites';

type ReportCardProps = {
  report: SiteReport;
};

export default function ReportCard({ report }: ReportCardProps) {
  const [open, setOpen] = useState(false);
  const detailsId = `report-${report.slug}`;

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-300 hover:border-primary-300">
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
        <img
          src={report.image}
          alt={report.title}
          title={`${report.title} — SGSCTIC`}
          className="h-full w-full object-cover object-top transition-transform duration-[900ms] ease-smooth group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 via-transparent to-transparent" />
        <span className="absolute left-4 top-4 rounded-full bg-background-50/95 px-3 py-1 font-label text-[10px] uppercase tracking-[0.14em] text-foreground-800">
          {report.date}
        </span>
        <span className="absolute bottom-4 left-4 flex items-center gap-2">
          <span className="h-1.5 w-1.5 bg-primary-400" />
          <span className="label-mono text-background-100">{report.sector}</span>
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h3 className="font-heading text-xl font-semibold uppercase leading-tight text-foreground-950 md:text-2xl">
          {report.title}
        </h3>
        <p className="mt-1.5 flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-foreground-500">
          <i className="ri-map-pin-2-line text-primary-500" />
          {report.site}
          <span className="text-background-400">·</span>
          {report.duration}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">{report.summary}</p>

        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          aria-controls={detailsId}
          className="mt-5 flex cursor-pointer items-center gap-2 self-start whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-primary-600 transition-colors hover:text-primary-700"
        >
          {open ? 'Réduire le compte rendu' : 'Lire le compte rendu'}
          <i
            className={`text-sm transition-transform duration-300 ${
              open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
            }`}
          />
        </button>

        {open ? (
          <div id={detailsId} className="mt-5 border-t border-background-200 pt-5">
            <div className="grid grid-cols-3 gap-px overflow-hidden rounded-md bg-background-200">
              {report.facts.map((fact) => (
                <div key={fact.label} className="bg-background-100 px-3 py-3">
                  <p className="text-[9px] uppercase leading-tight tracking-[0.12em] text-foreground-500">
                    {fact.label}
                  </p>
                  <p className="mt-1 text-xs font-semibold leading-tight text-foreground-950">
                    {fact.value}
                  </p>
                </div>
              ))}
            </div>

            <ul className="mt-4 space-y-2.5">
              {report.highlights.map((highlight) => (
                <li key={highlight} className="flex gap-2.5 text-xs leading-relaxed text-foreground-700">
                  <i className="ri-check-line mt-0.5 flex-shrink-0 text-primary-600" />
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </article>
  );
}